import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, Button, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Chip, IconButton, Alert, Checkbox, Tabs, Tab
} from '@mui/material';
import { AutoFixHigh, PersonAdd, Delete, Save } from '@mui/icons-material';
import { assignmentsAPI, reviewersAPI, applicationsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';

var E = React.createElement;

export default function AssignmentManager() {
  var params = useParams();
  var formId = params.id;
  var snack = useSnackbar();
  var enqueueSnackbar = snack.enqueueSnackbar;

  // State
  var cs = useState({ reviewers_per_application: 2, assignment_rule: 'round_robin', auto_assign_enabled: false });
  var config = cs[0]; var setConfig = cs[1];
  var as2 = useState([]); var assignments = as2[0]; var setAssignments = as2[1];
  var rs = useState([]); var reviewers = rs[0]; var setReviewers = rs[1];
  var aps = useState([]); var apps = aps[0]; var setApps = aps[1];
  var sr = useState(''); var selectedReviewer = sr[0]; var setSelectedReviewer = sr[1];
  var sel = useState([]); var selectedApps = sel[0]; var setSelectedApps = sel[1];
  var sa = useState([]); var selectedAssigned = sa[0]; var setSelectedAssigned = sa[1];
  var tab = useState(0); var activeTab = tab[0]; var setActiveTab = tab[1];
  var ld = useState(true); var loading = ld[0]; var setLoading = ld[1];

  function loadAll() {
    setLoading(true);
    Promise.all([
      assignmentsAPI.getConfig(formId),
      assignmentsAPI.list(formId),
      reviewersAPI.list('can_review'),
      loadAllApps()
    ]).then(function(results) {
      setConfig(results[0].data.data);
      setAssignments(results[1].data.data);
      setReviewers(results[2].data.data.filter(function(rv) { return rv.is_active && rv.invite_accepted; }));
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }

  // Load ALL applications by paginating (backend caps per_page at 100)
  function loadAllApps() {
    var allApps = [];
    var page = 1;
    function fetchPage() {
      return applicationsAPI.list(formId, { page: page, per_page: 100 }).then(function(r) {
        var data = r.data.data;
        if (!data || data.length === 0) { setApps(allApps); return; }
        allApps = allApps.concat(data);
        if (data.length < 100) { setApps(allApps); return; }
        page++;
        return fetchPage();
      });
    }
    return fetchPage();
  }

  useEffect(function() { loadAll(); }, [formId]);

  var saveConfig = function() {
    assignmentsAPI.saveConfig(formId, config).then(function() {
      enqueueSnackbar('Config saved', { variant: 'success' });
    }).catch(function() { enqueueSnackbar('Failed', { variant: 'error' }); });
  };

  var autoAssign = function() {
    assignmentsAPI.autoAssign(formId).then(function(res) {
      enqueueSnackbar(res.data.data.assignments_created + ' assignments created', { variant: 'success' });
      loadAll();
    }).catch(function(err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Auto-assign failed', { variant: 'error' });
    });
  };

  var removeAssignment = function(id) {
    assignmentsAPI.remove(id).then(function() {
      enqueueSnackbar('Removed', { variant: 'info' });
      loadAll();
    });
  };

  // Computed: apps assigned to selected reviewer & unassigned apps
  var assignedToReviewer = [];
  var unassignedApps = [];

  if (selectedReviewer) {
    var reviewerAssignedIds = {};
    assignments.forEach(function(a) {
      if (a.reviewer_id === selectedReviewer) reviewerAssignedIds[a.application_id] = true;
    });
    var allAssignedIds = {};
    assignments.forEach(function(a) { allAssignedIds[a.application_id] = true; });
    apps.forEach(function(app) {
      if (reviewerAssignedIds[app.id]) {
        assignedToReviewer.push(app);
      } else if (!allAssignedIds[app.id]) {
        unassignedApps.push(app);
      }
    });
  }

  // ── Bulk assign (unassigned tab) ──
  var handleBulkAssign = function() {
    if (!selectedReviewer || selectedApps.length === 0) return;
    assignmentsAPI.bulkAssign(selectedApps, selectedReviewer).then(function(r) {
      var d = r.data.data;
      enqueueSnackbar(d.assigned + ' assigned' + (d.skipped > 0 ? ', ' + d.skipped + ' skipped' : ''), { variant: 'success' });
      setSelectedApps([]);
      loadAll();
    }).catch(function(err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Bulk assign failed', { variant: 'error' });
    });
  };

  var toggleSelect = function(appId) {
    setSelectedApps(function(prev) {
      return prev.indexOf(appId) >= 0 ? prev.filter(function(x) { return x !== appId; }) : prev.concat([appId]);
    });
  };

  var selectAllUnassigned = function() {
    if (selectedApps.length === unassignedApps.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(unassignedApps.map(function(a) { return a.id; }));
    }
  };

  // ── Bulk remove (assigned tab) ──
  // Find the assignment ID for a given app + selected reviewer
  function getAssignmentId(appId) {
    for (var i = 0; i < assignments.length; i++) {
      if (assignments[i].application_id === appId && assignments[i].reviewer_id === selectedReviewer) {
        return assignments[i].id;
      }
    }
    return null;
  }

  var toggleSelectAssigned = function(assignmentId) {
    setSelectedAssigned(function(prev) {
      return prev.indexOf(assignmentId) >= 0 ? prev.filter(function(x) { return x !== assignmentId; }) : prev.concat([assignmentId]);
    });
  };

  var selectAllAssigned = function() {
    var allIds = [];
    assignedToReviewer.forEach(function(app) {
      var aid = getAssignmentId(app.id);
      if (aid) allIds.push(aid);
    });
    if (selectedAssigned.length === allIds.length) {
      setSelectedAssigned([]);
    } else {
      setSelectedAssigned(allIds);
    }
  };

  var handleBulkRemove = function() {
    if (selectedAssigned.length === 0) return;
    assignmentsAPI.bulkRemove(selectedAssigned).then(function(r) {
      var d = r.data.data;
      enqueueSnackbar(d.removed + ' assignments removed', { variant: 'success' });
      setSelectedAssigned([]);
      loadAll();
    }).catch(function(err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Bulk remove failed', { variant: 'error' });
    });
  };

  var selectedReviewerObj = reviewers.find(function(r) { return r.id === selectedReviewer; });
  var reviewerName = selectedReviewerObj ? selectedReviewerObj.name : '';

  // ── Render helpers ──

  function renderAssignedTable() {
    if (assignedToReviewer.length === 0) {
      return E(Typography, {color: 'text.secondary', sx: {py: 2}},
        'No applications assigned to this reviewer yet.');
    }

    return E(Box, null,
      // Bulk remove bar
      E(Box, {sx: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1}},
        E(Box, {sx: {display: 'flex', alignItems: 'center', gap: 1}},
          E(Checkbox, {
            checked: selectedAssigned.length === assignedToReviewer.length && assignedToReviewer.length > 0,
            indeterminate: selectedAssigned.length > 0 && selectedAssigned.length < assignedToReviewer.length,
            onChange: selectAllAssigned
          }),
          E(Typography, {variant: 'body2'},
            selectedAssigned.length > 0 ? selectedAssigned.length + ' selected' : 'Select all')),
        selectedAssigned.length > 0
          ? E(Button, {variant: 'contained', size: 'small', color: 'error',
              startIcon: E(Delete), onClick: handleBulkRemove},
              'Remove ' + selectedAssigned.length + ' from ' + reviewerName)
          : null),

      // Table
      E(TableContainer, null,
        E(Table, {size: 'small'},
          E(TableHead, null,
            E(TableRow, null,
              E(TableCell, {padding: 'checkbox'}),
              E(TableCell, null, 'Applicant'),
              E(TableCell, null, 'Email'),
              E(TableCell, null, 'Review Status'),
              E(TableCell, {align: 'right'}, 'Actions'))),
          E(TableBody, null,
            assignedToReviewer.map(function(app) {
              var assignmentId = getAssignmentId(app.id);
              var assignment = assignments.find(function(a) {
                return a.application_id === app.id && a.reviewer_id === selectedReviewer;
              });
              var isChecked = assignmentId && selectedAssigned.indexOf(assignmentId) >= 0;
              return E(TableRow, {key: app.id, hover: true, sx: {cursor: 'pointer'},
                onClick: function() { if (assignmentId) toggleSelectAssigned(assignmentId); }},
                E(TableCell, {padding: 'checkbox'},
                  E(Checkbox, {checked: !!isChecked, size: 'small',
                    onClick: function(e) { e.stopPropagation(); },
                    onChange: function() { if (assignmentId) toggleSelectAssigned(assignmentId); }})),
                E(TableCell, null, app.applicant_name || app.name || '—'),
                E(TableCell, null, app.applicant_email || app.email || '—'),
                E(TableCell, null,
                  assignment ? E(Chip, {label: assignment.review_status || 'pending', size: 'small',
                    color: assignment.review_status === 'completed' ? 'success'
                      : assignment.review_status === 'in_progress' ? 'warning' : 'default'}) : null),
                E(TableCell, {align: 'right'},
                  assignment ? E(IconButton, {size: 'small', color: 'error',
                    onClick: function(e) { e.stopPropagation(); removeAssignment(assignment.id); }},
                    E(Delete, {fontSize: 'small'})) : null));
            })))));
  }

  function renderUnassignedTable() {
    if (unassignedApps.length === 0) {
      return E(Typography, {color: 'text.secondary', sx: {py: 2}},
        'All applications have been assigned.');
    }

    return E(Box, null,
      // Bulk assign bar
      E(Box, {sx: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1}},
        E(Box, {sx: {display: 'flex', alignItems: 'center', gap: 1}},
          E(Checkbox, {
            checked: selectedApps.length === unassignedApps.length && unassignedApps.length > 0,
            indeterminate: selectedApps.length > 0 && selectedApps.length < unassignedApps.length,
            onChange: selectAllUnassigned
          }),
          E(Typography, {variant: 'body2'},
            selectedApps.length > 0 ? selectedApps.length + ' selected' : 'Select all')),
        selectedApps.length > 0
          ? E(Button, {variant: 'contained', size: 'small', startIcon: E(PersonAdd),
              onClick: handleBulkAssign},
              'Assign ' + selectedApps.length + ' to ' + reviewerName)
          : null),

      // Table
      E(TableContainer, null,
        E(Table, {size: 'small'},
          E(TableHead, null,
            E(TableRow, null,
              E(TableCell, {padding: 'checkbox'}),
              E(TableCell, null, 'Applicant'),
              E(TableCell, null, 'Email'),
              E(TableCell, null, 'Submitted'))),
          E(TableBody, null,
            unassignedApps.map(function(app) {
              var isChecked = selectedApps.indexOf(app.id) >= 0;
              return E(TableRow, {key: app.id, hover: true, sx: {cursor: 'pointer'},
                onClick: function() { toggleSelect(app.id); }},
                E(TableCell, {padding: 'checkbox'},
                  E(Checkbox, {checked: isChecked, size: 'small'})),
                E(TableCell, null, app.applicant_name || app.name || '—'),
                E(TableCell, null, app.applicant_email || app.email || '—'),
                E(TableCell, null, app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'));
            })))));
  }

  // ── Main render ──

  return E(Box, null,
    E(Typography, {variant: 'h4', sx: {mb: 3}}, 'Assignment Manager'),

    E(Grid, {container: true, spacing: 3},

      // Left panel: config + reviewer selector
      E(Grid, {item: true, xs: 12, md: 4},

        // Config card
        E(Card, null,
          E(CardContent, null,
            E(Typography, {variant: 'h6', sx: {mb: 2}}, 'Configuration'),
            E(TextField, {fullWidth: true, label: 'Reviewers per Application', type: 'number',
              value: config.reviewers_per_application,
              onChange: function(e) { setConfig(Object.assign({}, config, {reviewers_per_application: parseInt(e.target.value) || 1})); },
              sx: {mb: 2}, inputProps: {min: 1}}),
            E(FormControl, {fullWidth: true, sx: {mb: 2}},
              E(InputLabel, null, 'Assignment Rule'),
              E(Select, {value: config.assignment_rule, label: 'Assignment Rule',
                onChange: function(e) { setConfig(Object.assign({}, config, {assignment_rule: e.target.value})); }},
                E(MenuItem, {value: 'round_robin'}, 'Round Robin (balanced)'),
                E(MenuItem, {value: 'random'}, 'Random'),
                E(MenuItem, {value: 'manual'}, 'Manual Only'))),
            E(FormControlLabel, {
              control: E(Switch, {checked: config.auto_assign_enabled,
                onChange: function(e) { setConfig(Object.assign({}, config, {auto_assign_enabled: e.target.checked})); }}),
              label: 'Auto-assign on new submissions'}),
            E(Box, {sx: {mt: 2, display: 'flex', gap: 1}},
              E(Button, {variant: 'contained', startIcon: E(Save), onClick: saveConfig}, 'Save'),
              E(Button, {variant: 'outlined', startIcon: E(AutoFixHigh), onClick: autoAssign,
                disabled: config.assignment_rule === 'manual'}, 'Run Auto-Assign')))),

        // Reviewer selector card
        E(Card, {sx: {mt: 2}},
          E(CardContent, null,
            E(Typography, {variant: 'h6', sx: {mb: 2}}, 'Select Reviewer'),
            E(FormControl, {fullWidth: true},
              E(InputLabel, null, 'Reviewer'),
              E(Select, {value: selectedReviewer, label: 'Reviewer',
                onChange: function(e) {
                  setSelectedReviewer(e.target.value);
                  setSelectedApps([]);
                  setSelectedAssigned([]);
                }},
                reviewers.map(function(rv) {
                  var count = assignments.filter(function(a) { return a.reviewer_id === rv.id; }).length;
                  return E(MenuItem, {key: rv.id, value: rv.id},
                    rv.name + ' (' + rv.email + ') — ' + count + ' assigned');
                }))),
            selectedReviewerObj ? E(Box, {sx: {mt: 1}},
              E(Typography, {variant: 'body2', color: 'text.secondary'},
                'Showing assignments for ', E('strong', null, reviewerName))) : null)),

        E(Alert, {severity: 'info', sx: {mt: 2}},
          apps.length + ' total applications loaded. Conflict detection is automatic.')),

      // Right panel: tabs
      E(Grid, {item: true, xs: 12, md: 8},
        !selectedReviewer
          ? E(Card, null,
              E(CardContent, {sx: {textAlign: 'center', py: 6}},
                E(PersonAdd, {sx: {fontSize: 64, color: 'text.disabled', mb: 2}}),
                E(Typography, {variant: 'h6', color: 'text.secondary'},
                  'Select a reviewer from the left panel to manage their assignments')))
          : E(Card, null,
              E(Tabs, {value: activeTab, onChange: function(ev, v) { setActiveTab(v); },
                sx: {borderBottom: 1, borderColor: 'divider'}},
                E(Tab, {label: 'Assigned (' + assignedToReviewer.length + ')'}),
                E(Tab, {label: 'Unassigned (' + unassignedApps.length + ')'})),
              E(CardContent, null,
                activeTab === 0 ? renderAssignedTable() : renderUnassignedTable())))));
}
